// pages/students/View.tsx
import DetailView from "@/components/DetailView";
import { Spinner, ErrorMessage, CustomAlertDialog } from "@/components";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import StudentEditDialog from "@/components/formDialog/StudentEditDialog";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchStudentDetailById,
  type BatchStudentDetailType,
  type StudentType,
} from "@/services/student.service";
import {
  studentsKey,
  useDeleteStudentDetailMutation,
} from "@/hooks/api/student.queries";
import { useAlertDialog } from "@/hooks/useDialogState";
import { useState } from "react";
import { Pencil, Trash } from "lucide-react";

// ─── Profile Card ─────────────────────────────────────────────────────────────

const StudentProfileCard = ({
  student,
  email,
  phone,
}: {
  student: BatchStudentDetailType;
  email?: string;
  phone?: string;
}) => {
  const rollNo =
    student.current_enrollment?.roll_number ?? student.roll_number ?? "—";
  const initials = rollNo.slice(0, 2).toUpperCase();

  return (
    <Card className="border-gray-100 shadow-sm">
      <CardContent className="pb-8 pt-8">
        <div className="flex flex-col gap-6 sm:flex-row">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-xl font-bold text-white">
            {initials}
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-bold text-gray-900">{rollNo}</h2>
              {student.current_enrollment?.status && (
                <Badge
                  variant={
                    student.current_enrollment.status === "active"
                      ? "secondary"
                      : "destructive"
                  }
                  className="rounded-full px-3 font-medium capitalize"
                >
                  {student.current_enrollment.status}
                </Badge>
              )}
              {student.current_enrollment?.is_alumni && (
                <Badge
                  variant="outline"
                  className="rounded-full px-3 font-medium"
                >
                  Alumni
                </Badge>
              )}
              {student.is_open_to_work && (
                <Badge
                  variant="outline"
                  className="rounded-full px-3 font-medium text-green-600"
                >
                  Open to Work
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 gap-y-1 text-sm text-gray-500 sm:grid-cols-2">
              <span>📧 {email || "—"}</span>
              <span>📱 {phone || "—"}</span>
              {student.current_enrollment?.degree_name && (
                <span>🎓 {student.current_enrollment.degree_name}</span>
              )}
              {student.current_enrollment?.degree_type && (
                <span>
                  📄 {student.current_enrollment.degree_type.toUpperCase()}
                </span>
              )}
              {student.current_enrollment?.college_name && (
                <span>🏫 {student.current_enrollment.college_name}</span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Enrollment Card ──────────────────────────────────────────────────────────

const EnrollmentCard = ({
  title,
  enrollment,
}: {
  title: string;
  enrollment: BatchStudentDetailType["current_enrollment"];
}) => {
  if (!enrollment) return null;

  const rows = [
    { label: "Roll Number", value: enrollment.roll_number },
    { label: "Degree", value: enrollment.degree_name },
    { label: "Degree Type", value: enrollment.degree_type?.toUpperCase() },
    { label: "College", value: enrollment.college_name },
    { label: "Start Year", value: enrollment.start_year },
    { label: "End Year", value: enrollment.end_year ?? "Ongoing" },
    { label: "Status", value: enrollment.status },
    {
      label: "Enrolled",
      value: enrollment.created_at
        ? new Date(enrollment.created_at).toLocaleDateString()
        : null,
    },
  ].filter((r) => r.value != null && r.value !== "");

  return (
    <Card className="border-gray-100 shadow-sm">
      <CardHeader>
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-12 gap-y-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
          {rows.map(({ label, value }) => (
            <div key={label} className="flex flex-col gap-0.5">
              <dt className="text-xs font-medium text-gray-400">{label}</dt>
              <dd className="capitalize text-gray-700">{String(value)}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────

const StudentsView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const alertDialog = useAlertDialog();
  const deleteMutation = useDeleteStudentDetailMutation();

  const {
    data: student,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["students", "detail", id],
    queryFn: () => fetchStudentDetailById(id!),
    enabled: !!id,
  });

  // Email / phone are often missing from the detail response — fall back to the
  // cached student list row (which carries org_email & phone).
  const listRow = queryClient
    .getQueryData<{ students: StudentType[] }>(studentsKey.lists({}))
    ?.students.find((s) => s._id === id);

  const email =
    student?.org_email ?? student?.personal_email ?? listRow?.email ?? "";
  const phone = student?.phone ?? listRow?.phone ?? "";

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(id!, {
      onSuccess: () => {
        alertDialog.close();
        navigate(-1);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (isError || !student) {
    return (
      <ErrorMessage
        message="Failed to load student details."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <>
      <DetailView
        title="Students"
        backPath={-1}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(true)}
            >
              <Pencil size={14} className="mr-1" />
              Edit
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() =>
                alertDialog.open({
                  message: `Are you sure you want to delete this student (${student.current_enrollment?.roll_number ?? student.roll_number ?? "Unknown"})?`,
                  mantraId: id ?? "",
                  mantraTitle: "",
                  action: "delete",
                })
              }
            >
              <Trash size={14} className="mr-1" />
              Delete
            </Button>
          </div>
        }
      >
        <StudentProfileCard student={student} email={email} phone={phone} />
        <EnrollmentCard
          title="Current Enrollment"
          enrollment={student.current_enrollment}
        />
        {student.last_degree && (
          <EnrollmentCard
            title="Previous Degree"
            enrollment={student.last_degree}
          />
        )}
      </DetailView>

      {/* Edit Dialog */}
      <StudentEditDialog
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        studentId={id!}
        student={student}
        seed={{
          name: listRow?.name,
          email,
          phone,
          cgpa: listRow?.cgpa || undefined,
        }}
      />

      {/* Delete Confirm */}
      <CustomAlertDialog
        isOpen={alertDialog.state.isOpen}
        onOpenChange={(open) => !open && alertDialog.close()}
        title="Delete Student"
        description={alertDialog.state.message as string}
        confirmText={deleteMutation.isPending ? "Deleting..." : "Delete"}
        onConfirm={handleDeleteConfirm}
        onCancel={alertDialog.close}
      />
    </>
  );
};

export default StudentsView;
